import  { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Space,
  Button,
  Typography,
  Tabs,
  Table,
  Tag,
} from 'antd';
import {
  DollarOutlined,
  DownloadOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import {
  useTodaySummary,
  useMonthlyReport,
  useTopPerformers,
} from '../hooks/useServiceCharge';
import { useQuery } from 'react-query';
import { serviceChargeService } from '../services/serviceChargeService';
import {
  exportDailySummaryToExcel,
  exportMonthlyReportToExcel,
} from '../utils/excelExport';
import RevenueChart from '../components/serviceCharge/RevenueChart';
import TopPerformersChart from '../components/serviceCharge/TopPerformersChart';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

function ReportsServiceChargePage() {
  // ==================== STATE ====================
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [dateRange, setDateRange] = useState<[string, string]>([
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('month').format('YYYY-MM-DD'),
  ]);

  // ==================== QUERIES ====================
  const { data: todaySummary, isLoading: todayLoading } = useTodaySummary();
  
  const { data: monthlyReport, isLoading: monthlyLoading } = useMonthlyReport(
    selectedYear,
    selectedMonth
  );

  const { data: topPerformers, isLoading: topPerformersLoading } = useTopPerformers({
    startDate: dateRange[0],
    endDate: dateRange[1],
    limit: 10,
  });

  const { data: dateRangeReport, isLoading: dateRangeLoading } = useQuery({
    queryKey: ['dateRangeReport', dateRange],
    queryFn: () => serviceChargeService.getDateRangeReport({
      startDate: dateRange[0],
      endDate: dateRange[1],
    }),
  });


  // ==================== HANDLERS ====================
  const handleMonthChange = (date: any) => {
    if (date) {
      setSelectedYear(date.year());
      setSelectedMonth(date.month() + 1);
    }
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([
        dates[0].format('YYYY-MM-DD'),
        dates[1].format('YYYY-MM-DD'),
      ]);
    }
  };

  const handleExportDailySummary = () => {
    if (todaySummary?.data?.summary) {
      exportDailySummaryToExcel(
        todaySummary.data.summary,
        dayjs().format('YYYY-MM-DD')
      );
    }
  };

  const handleExportMonthlyReport = () => {
    if (monthlyReport?.data?.report) {
      exportMonthlyReportToExcel(
        monthlyReport.data.report,
        selectedYear,
        selectedMonth
      );
    }
  };

  // ==================== TABLE COLUMNS ====================
  const dailySummaryColumns: ColumnsType<any> = [
    {
      title: 'ພະນັກງານ',
      dataIndex: ['user', 'name'],
      key: 'userName',
    },
    {
      title: 'ປະເພດ',
      dataIndex: ['_id', 'type'],
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'service' ? 'blue' : 'green'}>
          {type === 'service' ? 'ບໍລິການ' : 'ແນະນຳ'}
        </Tag>
      ),
    },
    {
      title: 'ຈຳນວນ',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
    },
    {
      title: 'ລາຍຮັບ (ກີບ)',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (value: number) => value.toLocaleString(),
    },
  ];

  const topPerformersColumns: ColumnsType<any> = [
    {
      title: 'ອັນດັບ',
      key: 'rank',
      width: 80,
      render: (_: any, __: any, index: number) => (
        <Tag color={index < 3 ? 'gold' : 'default'}>#{index + 1}</Tag>
      ),
    },
    {
      title: 'ພະນັກງານ',
      dataIndex: ['user', 'name'],
      key: 'userName',
    },
    {
      title: 'ຈຳນວນລາຍການ',
      dataIndex: 'serviceCount',
      key: 'serviceCount',
      align: 'right',
    },
    {
      title: 'ລາຍຮັບລວມ (ກີບ)',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right',
      render: (value: number) => (
        <span style={{ fontWeight: 'bold', color: '#3f8600' }}>
          {value.toLocaleString()}
        </span>
      ),
    },
  ];

  // ==================== RENDER ====================
  return (
    <div>
      <Title level={2}>
        <LineChartOutlined /> ລາຍງານ & Dashboard
      </Title>

      <Tabs defaultActiveKey="today">
        {/* ==================== TODAY TAB ==================== */}
        <TabPane tab="ສະຫຼຸບວັນນີ້" key="today">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Summary Cards */}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Card loading={todayLoading}>
                  <Statistic
                    title="ລາຍຮັບວັນນີ້"
                    value={todaySummary?.data?.totals?.all || 0}
                    precision={0}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<DollarOutlined />}
                    suffix="ກີບ"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card loading={todayLoading}>
                  <Statistic
                    title="ຄ່າບໍລິການ"
                    value={todaySummary?.data?.totals?.service || 0}
                    precision={0}
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<DollarOutlined />}
                    suffix="ກີບ"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card loading={todayLoading}>
                  <Statistic
                    title="ຄ່າແນະນຳ"
                    value={todaySummary?.data?.totals?.recommend || 0}
                    precision={0}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<DollarOutlined />}
                    suffix="ກີບ"
                  />
                </Card>
              </Col>
            </Row>

            {/* Today's Summary Table */}
            <Card
              title="ລາຍລະອຽດວັນນີ້"
              extra={
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportDailySummary}
                  disabled={!todaySummary?.data?.summary?.length}
                >
                  Export Excel
                </Button>
              }
            >
              <Table
                columns={dailySummaryColumns}
                dataSource={todaySummary?.data?.summary || []}
                rowKey={(record) => `${record._id.userId}-${record._id.type}`}
                loading={todayLoading}
                pagination={false}
              />
            </Card>
          </Space>
        </TabPane>

        {/* ==================== MONTHLY TAB ==================== */}
        <TabPane tab="ລາຍງານລາຍເດືອນ" key="monthly">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Month Selector */}
            <Card>
              <Space>
                <DatePicker
                  picker="month"
                  value={dayjs().year(selectedYear).month(selectedMonth - 1)}
                  onChange={handleMonthChange}
                  format="MM/YYYY"
                />
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportMonthlyReport}
                  disabled={!monthlyReport?.data?.report?.length}
                >
                  Export Excel
                </Button>
              </Space>
            </Card>

            {/* Summary Cards */}
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Card loading={monthlyLoading}>
                  <Statistic
                    title={`ລາຍຮັບເດືອນ ${selectedMonth}/${selectedYear}`}
                    value={monthlyReport?.data?.totals?.all || 0}
                    precision={0}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<DollarOutlined />}
                    suffix="ກີບ"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card loading={monthlyLoading}>
                  <Statistic
                    title="ຄ່າບໍລິການ"
                    value={monthlyReport?.data?.totals?.service || 0}
                    precision={0}
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<DollarOutlined />}
                    suffix="ກີບ"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card loading={monthlyLoading}>
                  <Statistic
                    title="ຄ່າແນະນຳ"
                    value={monthlyReport?.data?.totals?.recommend || 0}
                    precision={0}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<DollarOutlined />}
                    suffix="ກີບ"
                  />
                </Card>
              </Col>
            </Row>

            {/* Monthly Report Table */}
            <Card title="ລາຍລະອຽດລາຍເດືອນ">
              <Table
                columns={dailySummaryColumns}
                dataSource={monthlyReport?.data?.report || []}
                rowKey={(record) => `${record._id.userId}-${record._id.type}`}
                loading={monthlyLoading}
                pagination={{ pageSize: 20 }}
              />
            </Card>
          </Space>
        </TabPane>

        {/* ==================== CHARTS TAB ==================== */}
        <TabPane tab="ກາຟ & ວິເຄາະ" key="charts">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Date Range Selector */}
            <Card>
              <RangePicker
                value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
                onChange={handleDateRangeChange}
                format="DD/MM/YYYY"
              />
            </Card>

            {/* Revenue Chart */}
            <Row gutter={16}>
              <Col xs={24} lg={12}>
                <RevenueChart
                  data={dateRangeReport?.data?.report || []}
                  type="bar"
                  title="ກາຟລາຍຮັບລາຍວັນ"
                  loading={dateRangeLoading}
                />
              </Col>
              <Col xs={24} lg={12}>
                <RevenueChart
                  data={dateRangeReport?.data?.report || []}
                  type="line"
                  title="ແນວໂນ້ມລາຍຮັບ"
                  loading={dateRangeLoading}
                />
              </Col>
            </Row>

            {/* Top Performers & Service Distribution */}
            <Row gutter={16}>
              <Col xs={24} lg={24}>
                <TopPerformersChart
                  data={topPerformers?.data?.topPerformers || []}
                  title="Top 10 ພະນັກງານຍອດນິຍົມ"
                  loading={topPerformersLoading}
                />
              </Col>
            </Row>

            {/* Top Performers Table */}
            <Card title="ລາຍລະອຽດພະນັກງານຍອດນິຍົມ">
              <Table
                columns={topPerformersColumns}
                dataSource={topPerformers?.data?.topPerformers || []}
                rowKey="_id"
                loading={topPerformersLoading}
                pagination={false}
              />
            </Card>
          </Space>
        </TabPane>
      </Tabs>
    </div>
  );
}

export default ReportsServiceChargePage;